import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@arcadeai/design-system/components/ui/atoms/card";
import { Link } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { useMemo } from "react";
import { CanProject } from "@/app/capabilities/components/can-project";
import { PROJECT } from "@/app/capabilities/permissions";
import { DataTable } from "@/components/custom/table/data-table";
import { DataTableContent } from "@/components/custom/table/data-table-content";
import type { Hook, Plugin } from "../../types";
import { systemColumns } from "./columns";

/** Plugin row enriched with its associated hooks so the table re-renders when hook status changes */
export type PluginRow = Plugin & {
  _pluginHooks: Hook[];
};

export type PluginTableMeta = {
  onHoverPlugin?: (pluginId: string | null) => void;
  onSelectPlugin?: (pluginId: string) => void;
};

type ConnectedSystemsSectionProps = {
  hooks?: Hook[];
  pluginsByBinding: {
    tenant: Plugin[];
    project: Plugin[];
  };
  onHoverPlugin?: (pluginId: string | null) => void;
  onSelectPlugin?: (pluginId: string) => void;
};

export const ConnectedSystemsSection = ({
  hooks,
  pluginsByBinding,
  onHoverPlugin,
  onSelectPlugin,
}: ConnectedSystemsSectionProps) => {
  // Combine all plugins for the table, tenant first then project
  const allPlugins = useMemo(
    () => [...pluginsByBinding.tenant, ...pluginsByBinding.project],
    [pluginsByBinding]
  );

  // Enrich each plugin with its hooks so the table data changes when hooks change
  const tableData: PluginRow[] = useMemo(
    () =>
      allPlugins.map((plugin) => ({
        ...plugin,
        _pluginHooks: hooks?.filter((h) => h.plugin_id === plugin.id) ?? [],
      })),
    [allPlugins, hooks]
  );

  const hasSystems = allPlugins.length > 0;

  const tableMeta: PluginTableMeta = useMemo(
    () => ({ onHoverPlugin, onSelectPlugin }),
    [onHoverPlugin, onSelectPlugin]
  );

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg">Extensions</CardTitle>
        {/* Create flow lets the user pick project vs tenant binding; gate on the
            project create token (the common case). Tenant create is enforced by
            the backend's org plugins_create on submit. */}
        <CanProject permission={PROJECT.plugins.create}>
          <Button
            render={
              <Link
                from="/orgs/$organizationId/projects/$projectId/contextual-access/new"
                to="."
              />
            }
            size="sm"
          >
            <Plus className="mr-2 size-4" />
            Add Extension
          </Button>
        </CanProject>
      </CardHeader>
      <CardContent>
        {hasSystems ? (
          <DataTable
            columns={systemColumns}
            data={tableData}
            defaultPageSize={10}
            getRowId={(row) => row.id}
            meta={tableMeta}
          >
            <DataTableContent stickyColumnBg="card" />
          </DataTable>
        ) : (
          <div className="py-8 text-center">
            <p className="text-muted-foreground text-sm">
              No extensions connected. Click "Add Extension" to add one.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
